// Zustand
import { create, StateCreator } from 'zustand';
import { persist, createJSONStorage, devtools } from 'zustand/middleware';
import { useCurrentUserStore } from 'store/UserStore';
import { pushDirtySettings } from 'api/resource/user';
import { settingsSchema } from '@billing/validation';
import { z } from 'zod';
import { getFiscalYearEndFromStartMonth } from 'utils/fiscalYear';

// Centralized user settings (UI + preference flags) that should live for the
// lifetime of the browser session and be re-hydrated from the API on login.
// NOTE: We intentionally use sessionStorage (NOT localStorage) so that a full
// browser restart clears potentially stale settings and we always refresh from
// the server at next login. If a setting should survive browser restarts move
// it to (or mirror it in) an existing store that uses localStorage.

export type ChartType = 'line' | 'bar' | 'area';
export type EditPageLayout = 'tabbed' | 'unified'; // 'tabbed' = segmented sections, 'unified' = single long form
export type ProviderDashboardSummaryMode = 'pay-period' | 'month';
export type ProviderDashboardDateBasis = 'billed-date' | 'service-date';
export type PersistedVisibleColumns = { [key: string]: boolean };

export const normalizeProviderDashboardPreferences = ({
  summaryMode,
  dateBasis,
}: {
  summaryMode: ProviderDashboardSummaryMode;
  dateBasis: ProviderDashboardDateBasis;
}) => ({
  providerDashboardSummaryMode: summaryMode,
  providerDashboardDateBasis:
    summaryMode === 'pay-period' ? 'billed-date' : dateBasis,
});

const filterColumnVisibility = (
  columns: PersistedVisibleColumns
): PersistedVisibleColumns => {
  return Object.fromEntries(
    Object.entries(columns).filter(([, visible]) => visible)
  );
};

export interface SettingsState {
  chartType: ChartType;
  editPageLayout: EditPageLayout;
  providerDashboardSummaryMode: ProviderDashboardSummaryMode;
  providerDashboardDateBasis: ProviderDashboardDateBasis;
  defaultSiteID: string;
  mfaPreference: boolean;
  persistedVisibleColumns: PersistedVisibleColumns;
  persistedColumnOrder: string[];
  persistedColumnSizing: Record<string, number>;
  providerClaimColumnLayoutVersion: number;
  applyProviderClaimColumnDefaults: (
    columns: PersistedVisibleColumns,
    order: string[]
  ) => void;
  fiscalYearEnd?: number; // 1-12
  // Track last successful sync timestamp (ms since epoch) with backend
  lastSyncedAt: number | null;
  dirty: boolean;
  defaultPayorID: string | null;

  setChartType: (type: ChartType) => void;
  setEditPageLayout: (layout: EditPageLayout) => void;
  setProviderDashboardSummaryMode: (mode: ProviderDashboardSummaryMode) => void;
  setProviderDashboardDateBasis: (basis: ProviderDashboardDateBasis) => void;
  setProviderDashboardPreferences: (preferences: {
    summaryMode: ProviderDashboardSummaryMode;
    dateBasis: ProviderDashboardDateBasis;
  }) => void;
  setDefaultSiteID: (siteID: string) => void;
  setMFAPreference: (enabled: boolean) => void;
  setPersistedVisibleColumns: (columns: PersistedVisibleColumns) => void;
  setPersistedColumnOrder: (order: string[]) => void;
  setPersistedColumnSizing: (
    sizing:
      | Record<string, number>
      | ((current: Record<string, number>) => Record<string, number>)
  ) => void;
  loadSettings: (partial: z.infer<typeof settingsSchema>) => void;
  setFiscalYearEnd: (month: number) => void;
  resetSettings: () => void;
  markSynced: () => void;
  markUnsynced: () => void;
  setDefaultPayorID: (payorID: string | null) => void;
}

export const isSettingsState = (u: any): u is SettingsState => {
  return settingsSchema.safeParse(u).success;
};

// Separate the state creator so we can pass it through both devtools & persist
const settingsStore: StateCreator<
  SettingsState,
  [['zustand/devtools', never], ['zustand/persist', unknown]]
> = (set) => ({
  chartType: 'bar',
  editPageLayout: 'tabbed',
  providerDashboardSummaryMode: 'pay-period',
  providerDashboardDateBasis: 'billed-date',
  defaultSiteID: '',
  mfaPreference: false,
  persistedVisibleColumns: {},
  persistedColumnOrder: [],
  persistedColumnSizing: {},
  providerClaimColumnLayoutVersion: 0,
  fiscalYearEnd: 12,
  lastSyncedAt: null,
  dirty: false,
  defaultPayorID: null,

  applyProviderClaimColumnDefaults: (columns, order) =>
    set({
      persistedVisibleColumns: filterColumnVisibility(columns),
      persistedColumnOrder: order,
      providerClaimColumnLayoutVersion: 1,
      dirty: true,
    }),
  setChartType: (type) =>
    set(() => ({
      chartType: type,
      dirty: true,
    })),
  setEditPageLayout: (layout) =>
    set(() => ({
      editPageLayout: layout,
      dirty: true,
    })),
  setProviderDashboardSummaryMode: (mode) =>
    set((state) => ({
      ...normalizeProviderDashboardPreferences({
        summaryMode: mode,
        dateBasis: state.providerDashboardDateBasis,
      }),
      dirty: true,
    })),
  setProviderDashboardDateBasis: (basis) =>
    set((state) => ({
      ...normalizeProviderDashboardPreferences({
        summaryMode: state.providerDashboardSummaryMode,
        dateBasis: basis,
      }),
      dirty: true,
    })),
  setProviderDashboardPreferences: ({ summaryMode, dateBasis }) =>
    set(() => ({
      ...normalizeProviderDashboardPreferences({
        summaryMode,
        dateBasis,
      }),
      dirty: true,
    })),
  setDefaultSiteID: (siteID) =>
    set(() => ({
      defaultSiteID: siteID,
      dirty: true,
    })),
  setMFAPreference: (enabled) =>
    set(() => ({
      mfaPreference: enabled,
      dirty: true,
    })),
  setPersistedVisibleColumns: (columns) => {
    set(() => ({
      persistedVisibleColumns: filterColumnVisibility(columns),
      dirty: true,
    }));
  },
  setPersistedColumnOrder: (order) => {
    set(() => ({
      persistedColumnOrder: order,
      dirty: true,
    }));
  },
  setPersistedColumnSizing: (sizing) =>
    set((state) => ({
      persistedColumnSizing:
        typeof sizing === 'function'
          ? sizing(state.persistedColumnSizing)
          : sizing,
      dirty: true,
    })),
  loadSettings: (partial) =>
    set((state) => {
      const hasSavedColumnLayout =
        partial.persistedVisibleColumns !== undefined ||
        partial.persistedColumnOrder !== undefined;
      const normalizedProviderDashboardPreferences =
        normalizeProviderDashboardPreferences({
          summaryMode:
            partial.providerDashboardSummaryMode ??
            state.providerDashboardSummaryMode,
          dateBasis:
            partial.providerDashboardDateBasis ??
            state.providerDashboardDateBasis,
        });

      return {
        chartType: partial.chartType ?? state.chartType,
        mfaPreference: partial.mfaPreference ?? state.mfaPreference,
        editPageLayout: partial.editPageLayout ?? state.editPageLayout,
        ...normalizedProviderDashboardPreferences,
        defaultSiteID: partial.defaultSiteID ?? state.defaultSiteID,
        persistedVisibleColumns: partial.persistedVisibleColumns
          ? filterColumnVisibility(partial.persistedVisibleColumns)
          : state.persistedVisibleColumns,
        persistedColumnOrder:
          partial.persistedColumnOrder ?? state.persistedColumnOrder,
        persistedColumnSizing:
          partial.persistedColumnSizing ?? state.persistedColumnSizing,
        providerClaimColumnLayoutVersion:
          partial.providerClaimColumnLayoutVersion ??
          (hasSavedColumnLayout ? 0 : state.providerClaimColumnLayoutVersion),
        fiscalYearEnd:
          partial.fiscalYearEnd ??
          (typeof (partial as any).fiscalStartMonth === 'number'
            ? getFiscalYearEndFromStartMonth((partial as any).fiscalStartMonth)
            : state.fiscalYearEnd),
        dirty: false,
        lastSyncedAt: state.lastSyncedAt,
        defaultPayorID:
          partial.defaultPayorID === undefined
            ? state.defaultPayorID
            : partial.defaultPayorID,
      };
    }),
  setFiscalYearEnd: (month) =>
    set(() => ({
      fiscalYearEnd: month,
      dirty: true,
    })),
  resetSettings: () =>
    set({
      chartType: 'bar',
      editPageLayout: 'tabbed',
      providerDashboardSummaryMode: 'pay-period',
      providerDashboardDateBasis: 'billed-date',
      defaultSiteID: '',
      persistedVisibleColumns: {},
      persistedColumnOrder: [],
      persistedColumnSizing: {},
      providerClaimColumnLayoutVersion: 0,
      mfaPreference: false,
      fiscalYearEnd: 12,
      lastSyncedAt: null,
      dirty: true,
      defaultPayorID: null,
    }),
  markSynced: () =>
    set(() => ({
      dirty: false,
      lastSyncedAt: Date.now(),
    })),
  markUnsynced: () =>
    set(() => ({
      dirty: true,
      lastSyncedAt: null,
    })),
  setDefaultPayorID: (payorID) =>
    set(() => ({
      defaultPayorID: payorID,
      dirty: true,
    })),
});

// Exported hook
export const useSettingsStore = create<SettingsState>()(
  devtools(
    persist(settingsStore, {
      name: 'settings-storage',
      storage: createJSONStorage(() => sessionStorage),
      version: 7,
      migrate: (persisted, version) => {
        if (!persisted) return {} as SettingsState;
        const data = { ...(persisted as any) };
        if (version < 2 && data.editPageLayout === undefined) {
          data.editPageLayout = 'tabbed';
        }
        if (version < 3 && data.defaultSiteID === undefined) {
          data.defaultSiteID = '';
        }
        if (version < 4 && data.fiscalYearEnd === undefined) {
          data.fiscalYearEnd =
            typeof data.fiscalStartMonth === 'number'
              ? getFiscalYearEndFromStartMonth(data.fiscalStartMonth)
              : 12;
          delete data.fiscalStartMonth;
        }
        if (version < 5 && data.providerDashboardSummaryMode === undefined) {
          data.providerDashboardSummaryMode = 'pay-period';
        }
        if (version < 6 && data.providerDashboardDateBasis === undefined) {
          data.providerDashboardDateBasis = 'billed-date';
        }
        if (version < 7 && data.defaultPayorID === undefined) {
          data.defaultPayorID = null;
        }
        if (
          data.providerDashboardSummaryMode !== undefined &&
          data.providerDashboardDateBasis !== undefined
        ) {
          Object.assign(
            data,
            normalizeProviderDashboardPreferences({
              summaryMode: data.providerDashboardSummaryMode,
              dateBasis: data.providerDashboardDateBasis,
            })
          );
        }
        if (data.persistedVisibleColumns) {
          data.persistedVisibleColumns = filterColumnVisibility(
            data.persistedVisibleColumns
          );
        }

        return data as SettingsState;
      },
      partialize: (state) => ({
        chartType: state.chartType,
        editPageLayout: state.editPageLayout,
        providerDashboardSummaryMode: state.providerDashboardSummaryMode,
        providerDashboardDateBasis: state.providerDashboardDateBasis,
        defaultSiteID: state.defaultSiteID,
        mfaPreference: state.mfaPreference,
        persistedVisibleColumns: state.persistedVisibleColumns,
        persistedColumnOrder: state.persistedColumnOrder,
        persistedColumnSizing: state.persistedColumnSizing,
        providerClaimColumnLayoutVersion:
          state.providerClaimColumnLayoutVersion,
        fiscalYearEnd: state.fiscalYearEnd,
        lastSyncedAt: state.lastSyncedAt,
        dirty: state.dirty,
        defaultPayorID: state.defaultPayorID,
      }),
    }),
    { name: 'SettingsStore', store: 'SettingsStore' }
  )
);
const getCurrentUserID = () => useCurrentUserStore.getState().id;

const getSettingsPayload = (state: SettingsState) => ({
  chartType: state.chartType,
  editPageLayout: state.editPageLayout,
  providerDashboardSummaryMode: state.providerDashboardSummaryMode,
  providerDashboardDateBasis: state.providerDashboardDateBasis,
  defaultSiteID: state.defaultSiteID,
  mfaPreference: state.mfaPreference,
  fiscalYearEnd: state.fiscalYearEnd,
  persistedVisibleColumns: state.persistedVisibleColumns,
  persistedColumnOrder: state.persistedColumnOrder,
  persistedColumnSizing: state.persistedColumnSizing,
  providerClaimColumnLayoutVersion: state.providerClaimColumnLayoutVersion,
  defaultPayorID: state.defaultPayorID,
});

export const getSettingsPayloadSignature = (state: SettingsState): string =>
  JSON.stringify(getSettingsPayload(state));

const saveLatestSettings = async (userID: string): Promise<boolean> => {
  if (getCurrentUserID() !== userID) return false;

  const state = useSettingsStore.getState();
  if (!state.dirty) return true;

  const payload = getSettingsPayload(state);
  const signature = JSON.stringify(payload);
  let success = false;
  try {
    success = await pushDirtySettings(userID, payload);
  } catch {
    // Keep the current preferences dirty so a later save can retry.
  }

  if (getCurrentUserID() !== userID) return false;
  if (!success) {
    useSettingsStore.getState().markUnsynced();
    return false;
  }
  if (getSettingsPayloadSignature(useSettingsStore.getState()) !== signature) {
    return saveLatestSettings(userID);
  }

  useSettingsStore.getState().markSynced();
  return true;
};

let settingsSaveQueue: Promise<boolean> = Promise.resolve(true);

export const syncSettingsWithBackend = (): Promise<boolean> => {
  const userID = getCurrentUserID();
  if (!userID) return Promise.resolve(false);

  settingsSaveQueue = settingsSaveQueue
    .catch(() => false)
    .then(() => saveLatestSettings(userID));
  return settingsSaveQueue;
};

// Sync settings when the user closes the tab or navigates away
// cant use async/await here so we do a best-effort sync
export const syncSettingsOnClose = () => {
  const userID = getCurrentUserID();
  if (!userID) return;
  if (!useSettingsStore.getState().dirty) return;
  void syncSettingsWithBackend();
};
